/* @jsx h */
import {
  FullScreenSheet,
  type FullScreenSheetSlotProps,
  LemmonModal,
  NumPad,
  useLocalization,
} from "@lemmonhq/uikit";
import { useNativeBridge } from "../../providers/NativeBridge";
import { useAuth } from "../../providers/Auth";
import {
  defineComponent,
  h,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import type { RecommendedTipSetting } from "@lemmonhq/types";

// ─── Northern Lights Background ───────────────────────────────────────────────

const NorthernLightsBackground = defineComponent({
  name: "NorthernLightsBackground",
  setup() {
    return () => (
      <div
        class="absolute inset-0 overflow-hidden"
        style="background: linear-gradient(170deg, #020817 0%, #071428 60%, #0c1a35 100%)"
      >
        <style>{`
          @keyframes nl-drift1 {
            0%,100% { transform: translateX(-15%) translateY(8%) rotate(-6deg) scaleX(1.3); opacity: 0.65; }
            40%     { transform: translateX(8%)  translateY(-12%) rotate(7deg) scaleX(1.6); opacity: 0.9; }
            70%     { transform: translateX(-5%) translateY(5%)  rotate(-2deg) scaleX(1.2); opacity: 0.55; }
          }
          @keyframes nl-drift2 {
            0%,100% { transform: translateX(18%) translateY(-6%) rotate(4deg) scaleX(1.4); opacity: 0.5; }
            35%     { transform: translateX(-12%) translateY(14%) rotate(-9deg) scaleX(1.1); opacity: 0.8; }
            65%     { transform: translateX(5%)  translateY(-3%) rotate(3deg) scaleX(1.5); opacity: 0.6; }
          }
          @keyframes nl-drift3 {
            0%,100% { transform: translateX(0%)  translateY(6%)  rotate(-3deg) scaleX(1.5); opacity: 0.45; }
            33%     { transform: translateX(-18%) translateY(-9%) rotate(7deg) scaleX(1.2); opacity: 0.75; }
            66%     { transform: translateX(14%) translateY(13%) rotate(-5deg) scaleX(1.7); opacity: 0.35; }
          }
          @keyframes nl-drift4 {
            0%,100% { transform: translateX(-8%) translateY(-4%) rotate(2deg) scaleX(1.2); opacity: 0.3; }
            50%     { transform: translateX(12%) translateY(8%)  rotate(-6deg) scaleX(1.6); opacity: 0.6; }
          }
          @keyframes nl-pulse {
            0%,100% { opacity: 0.15; }
            50%     { opacity: 0.35; }
          }
        `}</style>

        {/* Ambient base glow */}
        <div style="position:absolute;inset:0;background:radial-gradient(ellipse 80% 60% at 50% 80%, #0e3d6622 0%, transparent 70%)" />

        {/* Aurora band 1 – cyan-green */}
        <div
          style="position:absolute;width:160%;height:55%;top:15%;left:-30%;
                 background:linear-gradient(180deg,transparent 0%,#00ff8855 30%,#00e5cc44 60%,transparent 100%);
                 filter:blur(38px);border-radius:50%;
                 animation:nl-drift1 9s ease-in-out infinite"
        />

        {/* Aurora band 2 – violet-magenta */}
        <div
          style="position:absolute;width:145%;height:45%;top:5%;left:-22%;
                 background:linear-gradient(180deg,transparent 0%,#9b59ff55 35%,#e040fb33 65%,transparent 100%);
                 filter:blur(48px);border-radius:50%;
                 animation:nl-drift2 13s ease-in-out infinite"
        />

        {/* Aurora band 3 – teal-blue */}
        <div
          style="position:absolute;width:170%;height:40%;top:28%;left:-35%;
                 background:linear-gradient(180deg,transparent 0%,#00bcd444 40%,#1a6fff33 70%,transparent 100%);
                 filter:blur(42px);border-radius:50%;
                 animation:nl-drift3 11s ease-in-out infinite"
        />

        {/* Aurora band 4 – faint gold shimmer */}
        <div
          style="position:absolute;width:120%;height:30%;top:35%;left:-10%;
                 background:linear-gradient(180deg,transparent 0%,#ffd70022 50%,transparent 100%);
                 filter:blur(55px);border-radius:50%;
                 animation:nl-drift4 15s ease-in-out infinite"
        />

        {/* Horizon fade to dark */}
        <div style="position:absolute;bottom:0;left:0;right:0;height:35%;background:linear-gradient(0deg,#020817 0%,transparent 100%)" />
      </div>
    );
  },
});

// ─── Starry Night Background ──────────────────────────────────────────────────

const StarryNightBackground = defineComponent({
  name: "StarryNightBackground",
  setup() {
    // Deterministic-ish star placement seeded by index
    const stars = Array.from({ length: 55 }, (_, i) => {
      const seed = (i * 2654435761) >>> 0;
      return {
        id: i,
        x: ((seed * 1.1) % 97) + 1.5,
        y: ((seed * 0.7 + i * 13) % 98) + 1,
        size: (seed % 3) * 0.7 + 0.8,
        delay: (seed % 30) * 0.12,
        duration: (seed % 20) * 0.1 + 1.8,
        bright: seed % 7 === 0,
      };
    });

    return () => (
      <div
        class="absolute inset-0 overflow-hidden"
        style="background: linear-gradient(165deg, #02020f 0%, #050520 40%, #08102a 75%, #0a1220 100%)"
      >
        <style>{`
          @keyframes sn-twinkle {
            0%,100% { opacity: 0.15; transform: scale(1); }
            50%     { opacity: 1;    transform: scale(1.4); }
          }
          @keyframes sn-twinkle-bright {
            0%,100% { opacity: 0.4;  transform: scale(1); box-shadow: none; }
            50%     { opacity: 1;    transform: scale(1.6); box-shadow: 0 0 6px 2px #ffffffaa; }
          }
          @keyframes sn-shoot {
            0%   { transform: rotate(27deg) translateX(0);     opacity: 0.9; width: 2px; }
            75%  { transform: rotate(27deg) translateX(170px); opacity: 0.5; width: 60px; }
            100% { transform: rotate(27deg) translateX(220px); opacity: 0;   width: 80px; }
          }
          @keyframes sn-shoot2 {
            0%   { transform: rotate(211deg) translateX(0);    opacity: 0.8; width: 2px; }
            100% { transform: rotate(211deg) translateX(155px); opacity: 0;  width: 55px; }
          }
          @keyframes sn-moon-glow {
            0%,100% { box-shadow: 0 0 18px 6px #fff9c430, 0 0 40px 12px #fff9c418; }
            50%     { box-shadow: 0 0 28px 10px #fff9c450, 0 0 60px 20px #fff9c428; }
          }
          @keyframes sn-nebula {
            0%,100% { opacity: 0.18; }
            50%     { opacity: 0.30; }
          }
        `}</style>

        {/* Nebula glow patches */}
        <div
          style="position:absolute;width:60%;height:50%;top:5%;left:20%;
                 background:radial-gradient(ellipse, #1a0a4422 0%, transparent 70%);
                 filter:blur(30px);animation:sn-nebula 8s ease-in-out infinite"
        />
        <div
          style="position:absolute;width:50%;height:40%;top:30%;left:-5%;
                 background:radial-gradient(ellipse, #0a1a3322 0%, transparent 70%);
                 filter:blur(25px);animation:sn-nebula 11s ease-in-out infinite 2s"
        />

        {/* Stars */}
        {stars.map((s) => (
          <div
            key={s.id}
            style={{
              position: "absolute",
              left: `${s.x}%`,
              top: `${s.y}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              borderRadius: "50%",
              background: s.bright ? "#fffde7" : "#e8eaf6",
              animation: `${s.bright ? "sn-twinkle-bright" : "sn-twinkle"} ${s.duration}s ${s.delay}s ease-in-out infinite`,
            }}
          />
        ))}

        {/* Moon */}
        <div
          style="position:absolute;top:calc(16px + var(--safe-area-top,0px));right:16px;width:36px;height:36px;border-radius:50%;
                 background:radial-gradient(circle at 38% 36%, #fffde7, #fff9c4 60%, #f0e68c);
                 animation:sn-moon-glow 6s ease-in-out infinite"
        >
          {/* Moon shadow crater suggestion */}
          <div style="position:absolute;top:10px;left:8px;width:8px;height:6px;border-radius:50%;background:#e8d45022;filter:blur(1px)" />
        </div>

        {/* Shooting stars — rotated to match trajectory; gradient: transparent tail → white head */}
        <div
          style="position:absolute;top:18%;left:30%;height:1.5px;border-radius:2px;
                 background:linear-gradient(to right,transparent,#ffffff);
                 transform-origin:right center;
                 animation:sn-shoot 4.5s ease-in 2s infinite"
        />
        <div
          style="position:absolute;top:28%;left:65%;height:1px;border-radius:2px;
                 background:linear-gradient(to right,transparent,#ffffffcc);
                 transform-origin:right center;
                 animation:sn-shoot2 3.8s ease-in 7s infinite"
        />

        {/* Horizon fade */}
        <div style="position:absolute;bottom:0;left:0;right:0;height:30%;background:linear-gradient(0deg,#08102a 0%,transparent 100%)" />
      </div>
    );
  },
});

// ─── Randomly pick a background per mount ────────────────────────────────────

const BackgroundComponent = StarryNightBackground;
// Math.random() < 0.5 ? NorthernLightsBackground : StarryNightBackground;

// ─── Main component ───────────────────────────────────────────────────────────

// ─── Main component ───────────────────────────────────────────────────────────

interface AnimatedMobileTipsScreenProps {
  isOpen: boolean;
  baseAmount: number;
  selectedTipAmount: number;
  customTipAmount: number | null;
  isSubmitting: boolean;
  recommendedTips: RecommendedTipSetting;
  onClose: () => void;
  onQuickTip: (tipAmount: number) => void;
  onCustomTipChange: (value: number | null) => void;
  onSkip: () => void;
}

export const AnimatedMobileTipsScreen =
  defineComponent<AnimatedMobileTipsScreenProps>({
    name: "AnimatedMobileTipsScreen",
    props: [
      "isOpen",
      "baseAmount",
      "selectedTipAmount",
      "customTipAmount",
      "isSubmitting",
      "recommendedTips",
      "onClose",
      "onQuickTip",
      "onCustomTipChange",
      "onSkip",
    ],
    setup(props) {
      const { t } = useLocalization();
      const bridge = useNativeBridge();
      const auth = useAuth();
      const hasMadeSelection = ref(false);
      const isNumpadOpen = ref(false);
      const draftValue = ref("");
      const areBackgroundAnimationsRunning = ref(false);
      let backgroundAnimationTimeout: ReturnType<typeof setTimeout> | undefined;

      const scheduleBackgroundAnimations = () => {
        if (backgroundAnimationTimeout)
          clearTimeout(backgroundAnimationTimeout);
        areBackgroundAnimationsRunning.value = false;
        backgroundAnimationTimeout = setTimeout(() => {
          areBackgroundAnimationsRunning.value = true;
        }, 500);
      };

      onMounted(() => {
        if (props.isOpen) scheduleBackgroundAnimations();
      });

      onBeforeUnmount(() => {
        if (backgroundAnimationTimeout)
          clearTimeout(backgroundAnimationTimeout);
      });

      const sanitizeDecimal = (raw: string) => {
        const cleaned = raw.replace(/[^\d.]/g, "");
        const [integer, ...rest] = cleaned.split(".");
        return rest.length > 0 ? `${integer}.${rest.join("")}` : integer;
      };

      const openNumpad = () => {
        if (props.isSubmitting) return;
        draftValue.value =
          props.customTipAmount != null ? String(props.customTipAmount) : "";
        isNumpadOpen.value = true;
      };

      const closeNumpad = () => {
        isNumpadOpen.value = false;
      };

      const submitCustomTip = () => {
        const cleaned = sanitizeDecimal(draftValue.value);
        const parsed = cleaned ? parseFloat(cleaned) : null;
        const value =
          parsed != null && !Number.isNaN(parsed) && parsed >= 0
            ? parsed
            : null;
        hasMadeSelection.value = true;
        props.onCustomTipChange(value);
        closeNumpad();
      };

      watch(
        () => props.isOpen,
        (isOpen) => {
          if (isOpen) {
            hasMadeSelection.value = false;
            scheduleBackgroundAnimations();
          } else {
            if (backgroundAnimationTimeout)
              clearTimeout(backgroundAnimationTimeout);
            areBackgroundAnimationsRunning.value = false;
          }
        },
      );

      return () => {
        if (!props.isOpen) return null;

        return (
          <FullScreenSheet
            isOpen={props.isOpen}
            onClose={props.onClose}
            style="display:flex;flex-direction:column;overflow:hidden"
          >
            {{
              default: ({ close }: FullScreenSheetSlotProps) => (
                <>
                  <style>{`
            .amt-tip-btn {
              height: 80px;
              width: 100%;
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 0 24px;
              border-radius: 18px;
            }
            .amt-skip-btn {
              width: 100%;
              height: 56px;
              border-radius: 16px;
              border: 1.5px solid rgba(255,255,255,0.14);
              background: rgba(255,255,255,0.06);
              color: rgba(255,255,255,0.7);
              font-size: 16px;
              font-weight: 500;
              letter-spacing: 0.01em;
              cursor: pointer;
              transition: background 0.15s ease, color 0.15s ease;
              backdrop-filter: blur(8px);
            }
            .amt-skip-btn:active:not(:disabled) { background: rgba(255,255,255,0.12); color: #fff; }
            .amt-skip-btn:disabled { opacity: 0.5; cursor: not-allowed; }
            .amt-animated-background * { animation-play-state: paused !important; }
            .amt-animated-background.amt-animations-running * { animation-play-state: running !important; }
          `}</style>

                  {/* Full-screen wrapper */}
                  <div style="position:relative;flex:1;display:flex;flex-direction:column;overflow:hidden;background:#07101e">
                    <div
                      class={`amt-animated-background ${areBackgroundAnimationsRunning.value ? "amt-animations-running" : ""}`}
                      style="position:absolute;inset:0"
                    >
                      <BackgroundComponent />
                    </div>

                    {/* ── Top 25%: animated background ── */}
                    <div style="position:relative;height:25%;flex-shrink:0;overflow:hidden;z-index:1">
                      {/* Back button */}
                      <button
                        type="button"
                        onClick={close}
                        class="btn btn-ghost btn-sm btn-circle"
                        style="position:absolute;top:calc(12px + var(--safe-area-top,0px));left:16px;z-index:10;color:#fff"
                      >
                        <span class="material-symbols-outlined">
                          arrow_back
                        </span>
                      </button>

                      {/* Amount display centred in the animated area */}
                      <div
                        style="position:absolute;inset:0;display:flex;flex-direction:column;
                       align-items:center;justify-content:center;z-index:5;
                       text-shadow:0 2px 12px rgba(0,0,0,0.6)"
                      >
                        <div style="font-size:12px;letter-spacing:0.12em;text-transform:uppercase;color:rgba(255,255,255,0.55);margin-bottom:4px">
                          {t("checkout.tips.total_label")}
                        </div>
                        <div style="font-size:38px;font-weight:700;color:#fff;line-height:1">
                          {auth.user.value?.restaurant.currency || "CHF"}{" "}
                          {props.baseAmount.toFixed(2)}
                        </div>
                      </div>
                    </div>

                    {/* ── Bottom 75%: tip selection ── */}
                    <div
                      style="flex:1;overflow-y:auto;padding:20px 16px 28px;
                     display:flex;flex-direction:column;gap:12px;
                     position:relative;z-index:1;"
                    >
                      <div style="font-size:13px;letter-spacing:0.08em;text-transform:uppercase;color:rgba(255,255,255,0.4);margin-bottom:4px">
                        {t("checkout.tips.add_a_tip")}
                      </div>

                      {props.recommendedTips.steps.map((step, idx) => {
                        const { type } = props.recommendedTips;
                        const tipAmount =
                          type === "percentage"
                            ? Number(
                                ((props.baseAmount * step) / 100).toFixed(2),
                              )
                            : Number(step.toFixed(2));
                        const isSelected =
                          hasMadeSelection.value &&
                          props.customTipAmount == null &&
                          Math.abs(props.selectedTipAmount - tipAmount) < 0.001;

                        return (
                          <div key={step}>
                            <button
                              type="button"
                              class={`btn btn-accent amt-tip-btn ${isSelected ? "btn-active" : ""}`}
                              onClick={() => {
                                hasMadeSelection.value = true;
                                props.onQuickTip(tipAmount);
                              }}
                              disabled={props.isSubmitting}
                            >
                              <span style="font-size:26px;font-weight:700;line-height:1">
                                {type === "percentage"
                                  ? `${step}%`
                                  : `${auth.user.value?.restaurant.currency || "CHF"} ${step.toFixed(2)}`}
                              </span>
                              <span style="font-size:22px;font-weight:600">
                                {auth.user.value?.restaurant.currency || "CHF"}{" "}
                                {tipAmount.toFixed(2)}
                              </span>
                            </button>
                          </div>
                        );
                      })}

                      {/* Custom tip */}
                      <div>
                        <button
                          type="button"
                          class="btn btn-accent amt-tip-btn"
                          onClick={openNumpad}
                          disabled={props.isSubmitting}
                        >
                          <span style="font-size:26px;font-weight:700;line-height:1">
                            {t("checkout.tips.custom_tip_label")}
                          </span>
                          <span style="font-size:22px;font-weight:600">
                            {props.customTipAmount != null
                              ? `${auth.user.value?.restaurant.currency || "CHF"} ${props.customTipAmount.toFixed(2)}`
                              : "—"}
                          </span>
                        </button>

                        {isNumpadOpen.value && (
                          <LemmonModal
                            title={t("checkout.tips.custom_tip_placeholder")}
                            onClose={closeNumpad}
                          >
                            <div class="space-y-4 p-4">
                              <div class="mx-auto w-full max-w-[400px]">
                                <NumPad
                                  value={draftValue.value}
                                  onChange={(value: string) => {
                                    draftValue.value = sanitizeDecimal(value);
                                  }}
                                />
                              </div>
                              <button
                                type="button"
                                class="btn btn-accent h-11 w-full rounded-xl"
                                onClick={submitCustomTip}
                              >
                                Confirm
                              </button>
                            </div>
                          </LemmonModal>
                        )}
                      </div>

                      {/* Skip / No tip */}
                      <div style="margin-top:4px">
                        <button
                          type="button"
                          class="amt-skip-btn"
                          onClick={props.onSkip}
                          disabled={props.isSubmitting}
                        >
                          {props.isSubmitting
                            ? t("checkout.tips.starting_payment")
                            : t("checkout.tips.skip_tip")}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ),
            }}
          </FullScreenSheet>
        );
      };
    },
  });
